import i18n from 'i18next';
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { toast } from '../components/snackbar';
import { optimisticMutation } from './optimistic-mutation';

import type { SkillRecord } from '../sections/types';

export const SKILLS_QUERY_KEY = ['skills'] as const;

interface SkillsListResponse {
  skills: SkillRecord[];
  user_enabled_skill_ids: string[];
}

interface SkillInput {
  name: string;
  description?: string;
  instructions?: string;
}

interface SkillUpdateInput {
  name?: string;
  description?: string;
  instructions?: string;
}

export interface UseSkillsApi {
  fetchSkills: () => Promise<SkillsListResponse>;
  enableUserSkill: (skillId: string) => Promise<unknown>;
  disableUserSkill: (skillId: string) => Promise<unknown>;
  createSkill?: (data: SkillInput) => Promise<SkillRecord>;
  updateSkill?: (id: string, data: SkillUpdateInput) => Promise<SkillRecord>;
  deleteSkill?: (id: string) => Promise<void>;
}

export function useSkills(api: UseSkillsApi) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: SKILLS_QUERY_KEY,
    queryFn: api.fetchSkills,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const skillsCatalog = data?.skills ?? [];
  const userEnabledSkillIds = useMemo(
    () => new Set(data?.user_enabled_skill_ids ?? []),
    [data?.user_enabled_skill_ids]
  );

  const enableMutation = useMutation(
    optimisticMutation<unknown, string, SkillsListResponse>({
      queryClient,
      queryKey: SKILLS_QUERY_KEY,
      mutationFn: api.enableUserSkill,
      updater: (old, skillId) => {
        if (!old) return old;
        const ids = new Set(old.user_enabled_skill_ids);
        ids.add(skillId);
        return { ...old, user_enabled_skill_ids: [...ids] };
      },
    })
  );

  const disableMutation = useMutation(
    optimisticMutation<unknown, string, SkillsListResponse>({
      queryClient,
      queryKey: SKILLS_QUERY_KEY,
      mutationFn: api.disableUserSkill,
      updater: (old, skillId) => {
        if (!old) return old;
        return {
          ...old,
          user_enabled_skill_ids: old.user_enabled_skill_ids.filter((id) => id !== skillId),
        };
      },
    })
  );

  const createMutation = useMutation({
    mutationFn: api.createSkill ?? (() => Promise.reject(new Error('Not supported'))),
    onSuccess: (skill: SkillRecord) => {
      queryClient.setQueryData(SKILLS_QUERY_KEY, (old: SkillsListResponse | undefined) => {
        if (!old) return old;
        return { ...old, skills: [...old.skills, skill] };
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data: updateData }: { id: string; data: SkillUpdateInput }) =>
      (api.updateSkill ?? (() => Promise.reject(new Error('Not supported'))))(id, updateData),
    onMutate: async ({ id, data: updateData }) => {
      await queryClient.cancelQueries({ queryKey: SKILLS_QUERY_KEY });
      const previous = queryClient.getQueryData<SkillsListResponse>(SKILLS_QUERY_KEY);
      queryClient.setQueryData(SKILLS_QUERY_KEY, (old: SkillsListResponse | undefined) => {
        if (!old) return old;
        return {
          ...old,
          skills: old.skills.map((s) => (s.id === id ? { ...s, ...updateData } : s)),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(SKILLS_QUERY_KEY, context.previous);
      }
    },
    onSuccess: (skill: SkillRecord) => {
      // Reconcile with server truth (covers fields the server may normalize
      // — slug formatting, updated_at, etc.).
      queryClient.setQueryData(SKILLS_QUERY_KEY, (old: SkillsListResponse | undefined) => {
        if (!old) return old;
        return {
          ...old,
          skills: old.skills.map((s) => (s.id === skill.id ? skill : s)),
        };
      });
    },
  });

  // mutationFn takes the full { id, name } target so callers can read
  // mutation.variables.name on the failure path without a catalog lookup
  // (the catalog gets rolled back to include the row, but reading from
  // mutation.variables keeps view code simpler).
  const deleteMutation = useMutation(
    optimisticMutation<void, { id: string; name: string }, SkillsListResponse>({
      queryClient,
      queryKey: SKILLS_QUERY_KEY,
      mutationFn: ({ id }) => (api.deleteSkill ?? (() => Promise.reject(new Error('Not supported'))))(id),
      updater: (old, { id }) => {
        if (!old) return old;
        return {
          ...old,
          skills: old.skills.filter((s) => s.id !== id),
          user_enabled_skill_ids: old.user_enabled_skill_ids.filter((sid) => sid !== id),
        };
      },
    })
  );

  const createSkill = (input: SkillInput) => createMutation.mutateAsync(input);
  const deleteSkill = (target: { id: string; name: string }) => deleteMutation.mutateAsync(target);
  // The server refuses a skill that was never published. Say why here instead of a switch that snaps back.
  const enableSkill = (skillId: string) => {
    if (skillsCatalog.find((s) => s.id === skillId)?.live_state === 'not_published') {
      toast.error(i18n.t('skills.switchBlocked.body'));
      return;
    }
    enableMutation.mutate(skillId);
  };

  const uploadSkill = async (parsed: SkillInput) => {
    const existing = skillsCatalog.find((skill) => skill.name === parsed.name);
    if (existing) {
      throw new Error(i18n.t('skills.upload.duplicate'));
    }
    const created = await createSkill(parsed);
    enableSkill(created.id);
  };

  return {
    skillsCatalog,
    userEnabledSkillIds,
    isLoading,
    enableSkill,
    disableSkill: (skillId: string) => disableMutation.mutate(skillId),
    createSkill,
    updateSkill: (id: string, input: SkillUpdateInput) => updateMutation.mutateAsync({ id, data: input }),
    deleteSkill,
    /**
     * Exposed for views that want to surface in-flight or error state
     * without managing parallel local state. Read mutation.isError,
     * mutation.error, mutation.variables (the target that failed), and
     * call mutation.reset() to dismiss an error. See SkillsPageView for
     * the canonical confirm-dialog + error-recovery wiring.
     */
    deleteSkillMutation: deleteMutation,
    uploadSkill,
  };
}
